import { Injectable, inject, signal } from '@angular/core'
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular'
import {
  BrowserCacheLocation,
  AccountInfo,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser'
import { environment } from '../environment/environment'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoggedIn = signal(false)
  userEmail = signal('')
  userSid = ''
  private msalService = inject(MsalService)

  async login() {
    await this.msalService.instance.loginRedirect({
      redirectUri: `${window.location.origin}/`,
    } as RedirectRequest)
  }

  logout() {
    this.msalService.instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/`,
    })
  }

  setLoginDisplay() {
    const isLoggedIn = this.msalService.instance.getAllAccounts().length > 0
    this.isLoggedIn.set(isLoggedIn)
    if (!isLoggedIn) {
      this.userEmail.set('')
      this.userSid = ''
      return
    }

    const account =
      this.msalService.instance.getActiveAccount() ||
      this.msalService.instance.getAllAccounts()[0] ||
      null
    this.userEmail.set(this.getEmailFromAccount(account))
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount()

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts()
      const account = accounts[0]
      const sidToken = account.idTokenClaims?.sid
      if (sidToken !== undefined) {
        this.userSid = sidToken
      }
      this.msalService.instance.setActiveAccount(accounts[0])
      activeAccount = account
    }

    this.userEmail.set(this.getEmailFromAccount(activeAccount))
  }

  private getEmailFromAccount(account: AccountInfo | null) {
    const claims = account?.idTokenClaims as
      { email?: string; preferred_username?: string; upn?: string } | undefined

    return (
      claims?.email ||
      claims?.preferred_username ||
      claims?.upn ||
      account?.username ||
      ''
    )
  }

  async getAccessToken() {
    const account = this.msalService.instance.getActiveAccount()
    const accessTokenRequest = {
      scopes: [environment.apiScope],
      account: account,
    }

    const response = await this.msalService.instance.acquireTokenSilent(
      accessTokenRequest as SilentRequest
    )
    return response.accessToken
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: `${window.location.origin}/dashboard`,
      postLogoutRedirectUri: `${window.location.origin}/`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: true,
    },
  })
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map([['/api/*', [environment.apiScope]]])

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  }
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: './login-failed',
  }
}
